import type { Empresa } from '@/features/empresa/types/Empresa';
import type { UsuarioEmpresaSemUsuario } from '@/features/usuario/types/UsuarioEmpresa';
import { UsuarioPerfilTipoIdpk } from '@/features/usuario/types/enum';
import { queryClient } from '@/queryClient';
import { resetPaginationStores } from '@/std/pagination/createPaginationStore';
import type { LoginLogar } from '../types/LoginLogar';
import { EMPRESA_IDPK_ATUAL_STORAGE_KEY } from '../loginStorage';
import { useLoginStore } from '../store';

export function getEmpresaIdpk(): number | null {
    return useLoginStore.getState().empresaIdpk;
}

export function getLogin(): LoginLogar | null {
    return useLoginStore.getState().login;
}

export function getUsuarioEmpresa(): UsuarioEmpresaSemUsuario | null {
    const empresaIdpk = getEmpresaIdpk();
    if (!empresaIdpk) {
        return null;
    }

    const usuarioEmpresa = getLogin()?.usuario_empresa.find(
        (ue) => ue.use_empresa_idpk === empresaIdpk,
    );

    return usuarioEmpresa || null;
}

export function getEmpresa(): Omit<Empresa, 'empresa_slug'> | null {
    const empresa = getUsuarioEmpresa()?.empresa[0];
    return empresa || null;
}

export function isUsuarioLogadoVendedor(): { idpk: number; nome: string } | null {
    const usuarioEmpresa = getUsuarioEmpresa();
    if (!usuarioEmpresa || usuarioEmpresa.use_vendedor === 'N') {
        return null;
    }

    const login = getLogin();
    if (!login) {
        return null;
    }

    return {
        idpk: login.usu_idpk,
        nome: login.usu_nome,
    };
}

export function switchEmpresa(emp_idpk: number): void {
    const novaEmpresa = useLoginStore
        .getState()
        .login?.usuario_empresa.find((ue) => ue.use_empresa_idpk === emp_idpk);

    resetPaginationStores();

    useLoginStore.setState({
        empresaIdpk: emp_idpk,
        isFintech: novaEmpresa?.empresa[0]?.emp_sistema_tipo_uso === 'F',
    });

    if (novaEmpresa) {
        try {
            window.localStorage.setItem(EMPRESA_IDPK_ATUAL_STORAGE_KEY, String(emp_idpk));
        } catch {
            // A troca de empresa continua mesmo sem persistir a última empresa ativa.
        }
    }

    queryClient.clear();
}

export function updateEmpresa(empresa: Empresa): void {
    useLoginStore.setState((state) => {
        if (!state.login) {
            return state;
        }

        const updatedUsuarioEmpresa = state.login.usuario_empresa.map((ue) => {
            if (ue.use_empresa_idpk === empresa.emp_idpk) {
                return {
                    ...ue,
                    empresa: [empresa] as [Empresa],
                };
            }

            return ue;
        });

        return {
            login: {
                ...state.login,
                usuario_empresa: updatedUsuarioEmpresa,
            },
        };
    });
}

export function isUserAdminOrGerente(): boolean {
    const use_usuario_tipo_idpk = getUsuarioEmpresa()?.use_usuario_tipo_idpk;

    return (
        use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.Administrador ||
        use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.Gerente
    );
}

export function isUserAdmin(): boolean {
    const use_usuario_tipo_idpk = getUsuarioEmpresa()?.use_usuario_tipo_idpk;
    return use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.Administrador;
}

export function canUserListAllVendedores(): boolean {
    // Se não for vendedor, pode listar todos os vendedores
    if (!isUsuarioLogadoVendedor()) {
        return true;
    }

    // Se for vendedor, só pode listar se for avançado ou gerente ou admin

    const use_usuario_tipo_idpk = getUsuarioEmpresa()?.use_usuario_tipo_idpk;

    return (
        use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.Administrador ||
        use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.Gerente ||
        use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.Financeiro ||
        use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.FinanceiroAvancado ||
        use_usuario_tipo_idpk === UsuarioPerfilTipoIdpk.VendedorAvancado
    );
}

export function canUserListAllEntregadores(): boolean {
    return isUserAdminOrGerente();
}

export function canShowExtratoTemporario(keys: number[]): boolean {
    const empresa = getEmpresa();
    return !!empresa && keys.includes(empresa.emp_fintech_extrato_temporario);
}
